import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { DatabaseIcon, CloudArrowDownIcon, UploadSimpleIcon, PlusIcon } from "@phosphor-icons/react";

export default function Sources() {
  const { user } = useAuth();
  const [sources, setSources] = useState([]);
  const [ingestions, setIngestions] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ name: "", type: "csv", organisation: "", trust_tier: "medium", description: "" });
  const [busy, setBusy] = useState(null);
  const fileRefs = useRef({});
  const isAdmin = user?.role === "admin";

  const load = () => {
    api.get("/sources").then((r) => setSources(r.data));
    api.get("/ingestions").then((r) => setIngestions(r.data));
  };
  useEffect(() => { load(); }, []);

  const createSource = async (e) => {
    e.preventDefault();
    try {
      await api.post("/sources", form);
      toast.success("Source registered");
      setShowNew(false); setForm({ name: "", type: "csv", organisation: "", trust_tier: "medium", description: "" });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const pullOcpi = async (id) => {
    setBusy(id);
    try {
      const { data } = await api.post(`/sources/${id}/pull-ocpi`);
      toast.success(`OCPI pull: ${data.counts.ingested} ingested, ${data.counts.created} new, ${data.counts.discrepancies} conflicts`);
      load();
    } catch (e) { toast.error("Pull failed"); } finally { setBusy(null); }
  };

  const upload = async (id, file) => {
    if (!file) return;
    setBusy(id);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/sources/${id}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
      if (data.status === "processing") {
        toast.info(`Processing ${data.counts.received.toLocaleString()} records in the background…`);
        pollIngestion(data.id);
      } else {
        toast.success(`Uploaded: ${data.counts.ingested} ingested, ${data.counts.created} new, ${data.counts.rejected} rejected`);
        load();
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); } finally { setBusy(null); }
  };

  const pollIngestion = (ingestionId) => {
    const started = Date.now();
    const tick = async () => {
      try {
        const { data } = await api.get(`/ingestions/${ingestionId}`);
        if (data.status === "completed") {
          const c = data.counts;
          toast.success(`Ingestion complete: ${c.ingested.toLocaleString()} ingested, ${c.created.toLocaleString()} new, ${c.matched.toLocaleString()} matched, ${c.discrepancies.toLocaleString()} conflicts, ${c.rejected.toLocaleString()} rejected`);
          load();
          return;
        }
      } catch (e) { /* keep polling */ }
      if (Date.now() - started < 10 * 60 * 1000) setTimeout(tick, 2000);
    };
    setTimeout(tick, 2000);
  };

  return (
    <div className="p-5 lg:p-8 space-y-5">
      <div className="flex items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl lg:text-3xl font-semibold text-slate-900">Sources & Ingestion</h1>
          <p className="text-sm text-slate-500 mt-1">Register sources, trigger simulated OCPI pulls, upload CSV/JSON datasets.</p>
        </div>
        {isAdmin && (
          <button data-testid="new-source-btn" onClick={() => setShowNew(!showNew)} className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3.5 py-2 text-sm font-semibold text-white hover:bg-emerald-700 transition-colors">
            <PlusIcon size={16} /> New source
          </button>
        )}
      </div>

      {showNew && isAdmin && (
        <form onSubmit={createSource} className="rounded-xl border border-slate-200 bg-white p-4 grid sm:grid-cols-2 gap-3">
          <input data-testid="src-name" required placeholder="Source name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-emerald-500" />
          <input placeholder="Organisation" value={form.organisation} onChange={(e) => setForm({ ...form, organisation: e.target.value })} className="rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-emerald-500" />
          <select data-testid="src-type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-emerald-500">
            <option value="ocpi">OCPI (simulated)</option><option value="csv">CSV</option><option value="json">JSON</option><option value="government">Government</option>
          </select>
          <select value={form.trust_tier} onChange={(e) => setForm({ ...form, trust_tier: e.target.value })} className="rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-emerald-500">
            <option value="high">High trust</option><option value="medium">Medium trust</option><option value="low">Low trust</option>
          </select>
          <input placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="sm:col-span-2 rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-emerald-500" />
          <button data-testid="src-submit" type="submit" className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700">Register</button>
        </form>
      )}

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {sources.map((s) => (
          <div key={s.id} className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-100 text-slate-600"><DatabaseIcon size={18} weight="duotone" /></div>
                <div>
                  <div className="font-medium text-slate-900 leading-tight">{s.name}</div>
                  <div className="text-xs text-slate-400">{s.organisation}</div>
                </div>
              </div>
              <span className="rounded bg-slate-100 px-2 py-0.5 text-[10px] uppercase text-slate-500">{s.type}</span>
            </div>
            <div className="mt-3 flex items-center gap-2 text-xs text-slate-500">
              <span className="capitalize">{s.trust_tier} trust</span>·
              <span className="font-mono">{s.last_ingested_at ? new Date(s.last_ingested_at).toLocaleDateString() : "never ingested"}</span>
            </div>
            <div className="mt-4 flex gap-2">
              {s.type === "ocpi" ? (
                <button data-testid={`pull-${s.id}`} disabled={busy === s.id} onClick={() => pullOcpi(s.id)} className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-100 transition-colors disabled:opacity-50">
                  <CloudArrowDownIcon size={14} /> {busy === s.id ? "Pulling…" : "Pull OCPI feed"}
                </button>
              ) : (
                <>
                  <input type="file" accept=".csv,.json" hidden ref={(el) => (fileRefs.current[s.id] = el)} onChange={(e) => upload(s.id, e.target.files[0])} />
                  <button data-testid={`upload-${s.id}`} disabled={busy === s.id} onClick={() => fileRefs.current[s.id]?.click()} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 transition-colors disabled:opacity-50">
                    <UploadSimpleIcon size={14} /> {busy === s.id ? "Uploading…" : "Upload CSV/JSON"}
                  </button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100"><h2 className="font-display font-medium text-slate-900">Ingestion Log</h2></div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-400">
              <tr><th className="px-4 py-2.5">Source</th><th className="px-4 py-2.5">Trigger</th><th className="px-4 py-2.5">Received</th><th className="px-4 py-2.5">Ingested</th><th className="px-4 py-2.5">New</th><th className="px-4 py-2.5">Conflicts</th><th className="px-4 py-2.5">Rejected</th><th className="px-4 py-2.5">When</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {ingestions.map((i) => (
                <tr key={i.id} className="hover:bg-slate-50">
                  <td className="px-4 py-2.5 font-medium text-slate-800">{i.source_name}</td>
                  <td className="px-4 py-2.5 text-xs font-mono text-slate-500">{i.trigger}</td>
                  <td className="px-4 py-2.5 font-mono">{i.counts?.received}</td>
                  <td className="px-4 py-2.5 font-mono text-emerald-600">{i.counts?.ingested}</td>
                  <td className="px-4 py-2.5 font-mono">{i.counts?.created}</td>
                  <td className="px-4 py-2.5 font-mono text-amber-600">{i.counts?.discrepancies}</td>
                  <td className="px-4 py-2.5 font-mono text-rose-600">{i.counts?.rejected}</td>
                  <td className="px-4 py-2.5 font-mono text-xs text-slate-400">{new Date(i.started_at).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
